import { useNavigate } from "react-router-dom";
import DynamicFormPage from "../../components/DynamicFormPage";
import { getProductDetails } from "../../helpers/http_products_requests";
import { useParams } from "react-router-dom";
import { getCategories } from "../../helpers/http_categories_requests";
import { getFormats } from "../../helpers/http_formats_requests";
import { getComforts } from "../../helpers/http_comforts_requests";
import { getPlatings } from "../../helpers/http_platings_requests";
import { getExternalFinishings } from "../../helpers/http_external_finishings_requests";
import { useQuery } from "@tanstack/react-query";
import { getClaspTypes } from "../../helpers/http_clasp_types_requests";
import { getStones } from "../../helpers/http_stones_requests";
import { getStoneVariations } from "../../helpers/http_stone_variations_requests";

export default function ProductDetails() {

    const { id } = useParams();

    const navigate = useNavigate();

    const formatOptions = (data) => data?.data?.data?.data?.map(item => ({ value: item.id, label: item.name }));

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 5 });
    const formatsQuery = useQuery({ queryKey: ['getFormats'], queryFn: getFormats, staleTime: 1000 * 5 });
    const comfortsQuery = useQuery({ queryKey: ['getComforts'], queryFn: getComforts, staleTime: 1000 * 5 });
    const platingsQuery = useQuery({ queryKey: ['getPlatings'], queryFn: getPlatings, staleTime: 1000 * 5 });
    const externalFinishingsQuery = useQuery({ queryKey: ['getExternalFinishings'], queryFn: getExternalFinishings, staleTime: 1000 * 5 });
    const productQuery = useQuery({ queryKey: ['getProductDetails', id], queryFn: () => getProductDetails(id), staleTime: 1000 * 5 });
    const claspTypesQuery = useQuery({ queryKey: ['getClaspTypes'], queryFn: getClaspTypes, staleTime: 1000 * 5 });
    const stonesQuery = useQuery({ queryKey: ['getStones'], queryFn: getStones, staleTime: 1000 * 5 });

    const queries = [categoriesQuery, formatsQuery, comfortsQuery, platingsQuery, externalFinishingsQuery, productQuery, claspTypesQuery, stonesQuery];

    const isFetching = queries.some(query => query.isFetching);

    const categories = formatOptions(categoriesQuery);
    const formats = formatOptions(formatsQuery);
    const comforts = formatOptions(comfortsQuery);
    const platings = formatOptions(platingsQuery);
    const externalFinishings = formatOptions(externalFinishingsQuery);
    const claspTypes = formatOptions(claspTypesQuery);
    const stones = formatOptions(stonesQuery);

    const productDetails = productQuery?.data?.data?.data;

    const formFieldList = [
        {
            title: "Dados de Produto",
            fields: [
                {
                    name: "name",
                    label: "Nome",
                    type: "text",
                    required: true,
                },
                {
                    name: "category",
                    label: "Categoria",
                    type: "select",
                    required: true,
                    options: categories
                },
                {
                    name: "format",
                    label: "Formato",
                    type: "select",
                    options: formats
                },
                {
                    name: "confort",
                    label: "Conforto",
                    type: "select",
                    options: comforts
                },
                {
                    name: "height",
                    label: "Altura",
                    type: "number",
                    end_adornment: "mm"
                },
                {
                    name: "width",
                    label: "Largura",
                    type: "number",
                    end_adornment: "mm"
                },
                {
                    name: "thickness",
                    label: "Espessura",
                    type: "number",
                    end_adornment: "mm"
                },
                {
                    name: "length",
                    label: "Comprimento",
                    type: "number",
                    end_adornment: "cm"
                },
                {
                    name: "plating",
                    label: "Banho",
                    type: "select",
                    options: platings
                },
                {
                    name: "finishing",
                    label: "Acabamento Externo",
                    type: "select",
                    options: externalFinishings
                },
                {
                    name: "clasp_type",
                    label: "Fecho",
                    type: "select",
                    options: claspTypes
                },
                {
                    name: "is_crafted",
                    label: "Joia trabalhada / montada",
                    type: "check",
                },
                {
                    name: "is_active",
                    label: "Status",
                    type: "switch",
                },
                {
                    name: "public_description",
                    label: "Descrição Pública",
                    type: "text",
                    multiline: true,
                    rows: 5,
                    fullWidth: true,
                }
            ]
        },
        {
            title: "Pedras de Produto",
            fields: [
                {
                    name: "stones",
                    label: "Pedras",
                    type: "stones",
                    fullWidth: true,
                    modal_props: {
                        title: 'Inclusão de Pedras',
                        fields: [
                            {
                                title: 'Especificações',
                                fields: [
                                    {
                                        name: 'stone',
                                        label: 'Pedra',
                                        type: 'select_object',
                                        required: true,
                                        options: stones,
                                    },
                                    {
                                        name: 'variation',
                                        label: 'Variação',
                                        type: 'select_object',
                                        required: true,
                                        dependsOn: 'stone',
                                        dynamicOptions: (stoneId) => getStoneVariations(stoneId)
                                    },
                                    {
                                        name: 'quantity',
                                        label: 'Quantidade',
                                        type: 'number',
                                        required: true,
                                        end_adornment: 'unidades',
                                    },
                                    {
                                        name: "observation",
                                        label: "Observação",
                                        type: "text",
                                        multiline: true,
                                        rows: 5,
                                        fullWidth: true,
                                    }

                                ]
                            }
                        ]
                    }
                },
            ]
        },
        {
            title: "Pesos de Produto",
            fields: [
                {
                    name: "high_gold_weight",
                    label: "Peso em Ouro 18K",
                    type: "number",
                    required: true,
                    end_adornment: "g"
                },
                {
                    name: "low_gold_weight",
                    label: "Peso em Ouro 10K",
                    type: "number",
                    required: true,
                    end_adornment: "g"
                },
                {
                    name: "silver_weight",
                    label: "Peso em Prata 950",
                    type: "number",
                    required: true,
                    end_adornment: "g"
                },
            ]
        },
        {
            title: "Ficha Técnica",
            fields: [
                {
                    name: "self_reference",
                    label: "Referência Interna",
                    type: "text",
                    required: true,
                },
                {
                    name: "prod_time",
                    label: "Tempo Ideal de Produção",
                    type: "number",
                    required: true,
                    end_adornment: "dias"
                },
                {
                    name: "hard_weight_high_gold",
                    label: "Peso Bruto Ouro 18K",
                    type: "number",
                    required: true,
                    end_adornment: "g"
                },
                {
                    name: "hard_weight_low_gold",
                    label: "Peso Bruto Ouro 10K",
                    type: "number",
                    required: true,
                    end_adornment: "g"
                },
                {
                    name: "hard_weight_silver",
                    label: "Peso Bruto Prata 950",
                    type: "number",
                    required: true,
                    end_adornment: "g",
                    max_length: 1
                },
                {
                    name: "production_description",
                    label: "Descrição para Produção",
                    type: "text",
                    multiline: true,
                    rows: 5,
                    fullWidth: true,
                },
                {
                    name: "prod_steps_json",
                    label: "Passos de Produção",
                    type: "steps",
                    fullWidth: true,
                },
            ]
        }
    ];

    return <DynamicFormPage isLoading={isFetching} details initialData={productDetails} title={"Produtos - Detalhes"} formFieldList={formFieldList} multiImageUpload onCancel={() => navigate(-1)} />;
};
